import { Maximize, Minimize } from 'lucide-react';
import { useEffect, useState } from 'react';

type FullscreenToggleProps = {
    compact?: boolean;
};

export default function FullscreenToggle({ compact = false }: FullscreenToggleProps) {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [supported, setSupported] = useState(false);

    useEffect(() => {
        setSupported(typeof document.documentElement.requestFullscreen === 'function');

        function onChange() {
            setIsFullscreen(Boolean(document.fullscreenElement));
        }
        onChange();
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    async function toggleFullscreen() {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await document.documentElement.requestFullscreen();
            }
        } catch {
            // Denied or unsupported in this context — nothing else to do.
        }
    }

    if (!supported) return null;

    const Icon = isFullscreen ? Minimize : Maximize;
    const label = isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen';

    return (
        <button
            type="button"
            onClick={() => void toggleFullscreen()}
            className={`button-secondary inline-flex items-center justify-center gap-2 rounded-full text-sm font-medium ${compact ? 'h-11 w-11 px-0 py-0' : 'px-4 py-2'}`}
            aria-pressed={isFullscreen}
            aria-label={label}
            title={compact ? label : undefined}
        >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {compact ? null : label}
        </button>
    );
}
